import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoMark } from '../ui/LogoMark';

// "Beacon" launch loader (§17a): the mark draws itself, the light pulses, and a soft beam sweeps
// the screen while short status lines rotate. Shown while config, session and profile load.

export function Beacon({ failed = false }: { failed?: boolean }) {
  const { t } = useTranslation();
  const lines = t('boot.lines', { returnObjects: true }) as string[];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % lines.length), 1800);
    return () => clearInterval(timer);
  }, [lines.length]);

  return (
    <div
      className="fixed inset-0 grid place-items-center overflow-hidden bg-bg"
      role="status"
      aria-live="polite"
    >
      <Aurora intensity="soft" />
      {/* Beam: a cone of light anchored at the mark's light, sweeping like a lighthouse. */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[140vmax] w-[140vmax] -translate-x-1/2 -translate-y-1/2">
        <div
          className="absolute left-1/2 top-1/2 h-1/2 w-[28vmax] origin-top -translate-x-1/2 animate-beam"
          style={{
            background:
              'conic-gradient(from 180deg at 50% 0%, transparent 42%, color-mix(in oklab, var(--accent) 38%, transparent) 50%, transparent 58%)',
            filter: 'blur(18px)',
          }}
        />
      </div>
      <div className="relative flex flex-col items-center gap-6">
        <motion.div
          initial={{ scale: 0.86, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          {/* Light behind the mark (z-0), the mark above it (z-10). */}
          <div
            className="absolute inset-0 -z-0 -m-14 rounded-full opacity-60 blur-2xl animate-pulse-light"
            style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 65%)' }}
          />
          <LogoMark size={92} draw className="relative z-10" />
        </motion.div>
        <div className="h-6 text-center text-md text-muted">
          {failed ? (
            <p className="text-danger">{t('boot.failed')}</p>
          ) : (
            <AnimatePresence mode="wait">
              <motion.p
                key={index}
                initial={{ y: 8, opacity: 0, filter: 'blur(4px)' }}
                animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
                exit={{ y: -8, opacity: 0, filter: 'blur(4px)' }}
                transition={{ duration: 0.35 }}
              >
                {lines[index]}
              </motion.p>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}

/** Slow drifting glows in the theme's colours; used behind Beacon and the sign-in screen. */
export function Aurora({ intensity = 'full' }: { intensity?: 'soft' | 'full' }) {
  const opacity = intensity === 'soft' ? 'opacity-40' : 'opacity-70';
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${opacity}`}
    >
      <div className="absolute -left-[20%] -top-[30%] h-[70vmax] w-[70vmax] rounded-full bg-glow-1/45 blur-[110px] animate-drift-a" />
      <div className="absolute -right-[25%] top-[10%] h-[60vmax] w-[60vmax] rounded-full bg-glow-2/40 blur-[120px] animate-drift-b" />
      <div className="absolute -bottom-[35%] left-[15%] h-[65vmax] w-[65vmax] rounded-full bg-glow-3/35 blur-[130px] animate-drift-c" />
      {/* Fine grain keeps large gradients from banding and adds texture. */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
}
